import { z } from 'zod';

import {
  connectorKeySchema,
  connectorTextSchema,
  externalIdSchema,
  jsonScalarSchema,
  telegramIdSchema,
} from './common';

/**
 * POST {project.actionUrl}
 *
 * Оператор нажал кнопку из context.actions — мы транслируем нажатие в проект.
 * Действие выполняет проект: одобряет вывод, начисляет бонус, блокирует счёт.
 * Платформа не знает и не должна знать, что за этим стоит.
 */

/**
 * Кто нажал. Проект пишет это в свой аудит: у нас в AuditLog запись тоже
 * появится, но разбирать инцидент с деньгами придётся на стороне проекта.
 */
export const actionOperatorSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(120),
  email: z.email(),
});
export type ActionOperator = z.infer<typeof actionOperatorSchema>;

/**
 * Значения параметров — только скаляры. Форма из ActionParamSpec ничего
 * сложнее произвести не может, а произвольный вложенный JSON здесь означал бы,
 * что кто-то передаёт мимо контракта.
 */
export const actionParamsSchema = z.record(z.string(), jsonScalarSchema);
export type ActionParams = z.infer<typeof actionParamsSchema>;

export const actionRequestSchema = z.object({
  /** ContextAction.key того действия, которое нажали. */
  action: connectorKeySchema,
  /** Есть, если контакт связан. Проект вправе требовать его для действий с деньгами. */
  external_id: externalIdSchema.optional(),
  telegram_id: telegramIdSchema,
  params: actionParamsSchema.default({}),
  operator: actionOperatorSchema,
});
export type ActionRequest = z.infer<typeof actionRequestSchema>;
export type ActionRequestInput = z.input<typeof actionRequestSchema>;

export const actionResponseSchema = z.object({
  ok: z.boolean(),
  /** Показывается оператору как есть — и при успехе, и при отказе. */
  message: connectorTextSchema.optional(),
  /**
   * Перечитать карточку. Проект сам решает: начисление бонуса меняет баланс,
   * а отправка письма — нет. Без этого флага мы бы сбрасывали кэш контекста
   * после каждого нажатия.
   */
  refresh: z.boolean().default(false),
});
export type ActionResponse = z.infer<typeof actionResponseSchema>;
export type ActionResponseInput = z.input<typeof actionResponseSchema>;

/**
 * Тело запроса из панели оператора в наш API (web → api), откуда уже собирается
 * ActionRequest в проект. Здесь camelCase: это внутренний DTO, а не контракт.
 * telegram_id и operator подставляет сервер — клиент их не диктует.
 */
export const panelActionRequestSchema = z.object({
  actionKey: connectorKeySchema,
  params: actionParamsSchema.default({}),
});
export type PanelActionRequest = z.infer<typeof panelActionRequestSchema>;
